import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import socratesBust from '@/assets/socrates-philosopher-bust.png';

export default function About() {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-stone-300">
      <div className="sticky top-0 z-50 pt-[env(safe-area-inset-top)] bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="py-3">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-300 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-12 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 mt-4">
          <div className="w-16 h-16 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center overflow-hidden shrink-0">
            <img src={socratesBust} alt="Socrates" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white font-caveat">Socrates</h1>
            <p className="text-stone-500 text-sm">The simplest global football competition among friends</p>
          </div>
        </div>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <p>
              Fantasy sports originated in the United States and were architected around the
              statistical structures of baseball and American football. Their mechanics reward
              individual accumulation, auction-based roster construction, salary cap optimization,
              and volatility-driven outcomes. These systems function effectively within those
              sporting contexts because they mirror the statistical density and individual
              segmentation of those games. When applied to football, however, the fit becomes
              strained and breaks down.
            </p>
          </section>

          <section>
            <p>
              Socrates aims to be the simplest global football competition among friends,
              designed in deliberate contrast to the escalating complexity of modern sports
              gaming. Contemporary gaming platforms increasingly compete for engagement through
              accumulation: more stats, more wagers, more stimuli, more fragmentation. In that
              environment, attention is monetized through noise. Football, however, does not
              require additional layers of abstraction. It requires clarity.
            </p>
          </section>

          <section>
            <p>
              Across competitions such as the EPL, La Liga, Serie A, Bundesliga, and Ligue 1,
              football is not experienced as a sequence of isolated performances. It is a
              season-long coordinated expression of tactical systems, team sacrifice and identity.
              Although global football has embraced advanced analytics over the past two decades,
              the meaning of competition still resolves to a singular outcome: the result. Whether
              a team wins, draws, or loses. Trophies are not awarded for shots on goal or minutes
              played; they are awarded for results.
            </p>
          </section>

          <section>
            <p>
              Socrates is grounded in this premise. Its guiding inquiry is simple: if everything
              extraneous is removed, what remains essential?
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">How to Play</h2>
            <div className="space-y-4">
              <div>
                <p className="text-stone-200 font-medium">1/ Form a League</p>
                <p className="mt-1">
                  Users form a private league with friends and select a league from more than
                  150 available options within the app.
                </p>
              </div>
              <div>
                <p className="text-stone-200 font-medium">2/ Pick a Team</p>
                <p className="mt-1">
                  Each match week, every participant chooses one team from that league to back.
                  There are no player rosters, no transfers, no salary caps, no waiver systems,
                  and no lineup management.
                </p>
              </div>
              <div>
                <p className="text-stone-200 font-medium">3/ Earn Points</p>
                <p className="mt-1">
                  Points are awarded automatically based on the team's actual result:
                </p>
                <ul className="mt-2 space-y-1 text-stone-400">
                  <li>Win = <span className="text-stone-200 font-medium">3 points</span></li>
                  <li>Draw = <span className="text-stone-200 font-medium">1 point</span></li>
                  <li>Loss = <span className="text-stone-200 font-medium">0 points</span></li>
                  <li>Red card = <span className="text-red-400 font-medium">&minus;2 points</span></li>
                  <li>Goal difference &gt; 3 = <span className="text-stone-200 font-medium">+1 point</span></li>
                </ul>
              </div>
              <div>
                <p className="mt-2">
                  Additionally, each participant may elect to "Double Down" four times per season,
                  multiplying that week's point total, whether positive or negative. Standings
                  update in a table format consistent with global football conventions.
                </p>
              </div>
            </div>
          </section>

          <section className="border-t border-stone-700 pt-6">
            <p>
              In an era defined by overcomplexity, Socrates proposes restraint. It seeks to
              reestablish alignment between football as it is played and football as it is
              gamed. By returning to first principles and anchoring competition in the universal
              language of results, Socrates offers a format that is socially intuitive,
              strategically demanding, and faithful to the essence of the sport.
            </p>
            <p className="mt-4 text-stone-200 font-medium italic">
              Socrates does not attempt to gamify football; it mirrors it.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-stone-700 text-center text-stone-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Socrates. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
